import { Route, Switch } from 'react-router-dom';

import { FreeTextbooksCTANavbar } from 'ms-pages/FreeTextbooks/FreeTextbooksCTANavbar';
import { FreeTextbooksExploreOtherCurriculums } from 'ms-pages/FreeTextbooks/FreeTextbooksExploreOtherCurriculums/FreeTextbooksExploreOtherCurriculums';
import { FreeTextbooksFooterLinks } from 'ms-pages/FreeTextbooks/FreeTextbooksSubtopicViewFooter/FreeTextbooksFooterLinks';
import { FreeTextbooksSyllabusSelectorCountrySelector } from 'ms-pages/FreeTextbooks/FreeTextbooksSyllabusSelectorCountrySelector/FreeTextbooksSyllabusSelectorCountrySelector';
import FreeTextbooksSyllabusSelectorCurriculumSelector from 'ms-pages/FreeTextbooks/FreeTextbooksSyllabusSelectorCurriculumSelector/FreeTextbooksSyllabusSelectorCurriculumSelector';
import { FreeTextbooksSyllabusSelectorFooter } from 'ms-pages/FreeTextbooks/FreeTextbooksSyllabusSelectorFooter/FreeTextbooksSyllabusSelectorFooter';
import FreeTextbooksSyllabusSelectorTextbookSelector from 'ms-pages/FreeTextbooks/FreeTextbooksSyllabusSelectorTextbookSelector/FreeTextbooksSyllabusSelectorTextbookSelector';
import { DesktopOnly } from 'ms-pages/FreeTextbooks/Layout/Layout';
import type { CountryCode } from 'ms-pages/FreeTextbooks/types';
import { breakPoints, font, fontSize, lineHeight } from 'ms-styles/base';
import { colors } from 'ms-styles/colors';
import Separator from 'ms-ui-primitives/Separator';
import {
  minBreak,
  styled,
  multilineTextOverflow,
  tappable,
} from 'ms-utils/emotion';

import { FeaturesSection } from '../FreeTextbooksSubtopicViewFooter/FreeTextbooksFeaturesSection';
import {
  freeTextbooksCurriculumSelectorPath,
  freeTextbooksRootSelector,
  freeTextbooksTextbookSelectorPath,
} from '../urls';

// TODO have a unique definition for these ones
const PADDING = 48;
const PADDING_SMALL_SCREENS = 16;

const Wrapper = styled({
  width: '100%',
  height: '100%',
  overflow: 'auto',
});

const ContentWrapper = styled({
  maxWidth: breakPoints.large,
  margin: 'auto',
  paddingLeft: PADDING_SMALL_SCREENS,
  paddingRight: PADDING_SMALL_SCREENS,

  ...minBreak(breakPoints.small, {
    paddingLeft: PADDING,
    paddingRight: PADDING,
  }),
});

const FooterBackground = styled({
  marginTop: 32,
  backgroundColor: colors.neutralGray,
});

const SubTitle = styled(
  {
    ...multilineTextOverflow(2),
    ...font.gilroyExtraLargeHeading,
    ...tappable,
    fontSize: fontSize.large,
    lineHeight: lineHeight.title,
    color: colors.almond50,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 40,
    paddingBottom: 40,
  },
  'h2',
);

type Props = {
  countryCode: CountryCode;
};

export function FreeTextbooksSyllabusSelector({ countryCode }: Props) {
  return (
    <Wrapper>
      <DesktopOnly>
        <div style={{ display: 'flex', margin: 24 }}>
          <div
            style={{
              display: 'flex',
              marginLeft: 'auto',
              whiteSpace: 'nowrap',
            }}
          >
            <FreeTextbooksCTANavbar context="subtopic_detail" />
          </div>
        </div>
      </DesktopOnly>
      <ContentWrapper>
        <Switch>
          <Route exact path={freeTextbooksRootSelector}>
            <FreeTextbooksSyllabusSelectorCountrySelector />
          </Route>
          <Route exact path={freeTextbooksCurriculumSelectorPath}>
            <FreeTextbooksSyllabusSelectorCurriculumSelector
              countryCode={countryCode}
            />
          </Route>
          <Route
            exact
            path={freeTextbooksTextbookSelectorPath}
            render={({ match: { params } }) => (
              <FreeTextbooksSyllabusSelectorTextbookSelector
                curriculumId={params.curriculumId}
                countryCode={countryCode}
              />
            )}
          />
        </Switch>
      </ContentWrapper>

      {/* FOOTER */}
      <Route exact path={freeTextbooksCurriculumSelectorPath}>
        <FooterBackground>
          <ContentWrapper>
            <Separator size={8} />
            <FreeTextbooksSyllabusSelectorFooter countryCode={countryCode} />
          </ContentWrapper>

          <ContentWrapper>
            <SubTitle>What is Mathspace</SubTitle>
            <FeaturesSection isDarkMode />
            <Separator size={10} />
            <FreeTextbooksFooterLinks />
          </ContentWrapper>
        </FooterBackground>
      </Route>
      <Route exact path={freeTextbooksTextbookSelectorPath}>
        <FooterBackground>
          <ContentWrapper>
            <Separator size={8} />
            <FreeTextbooksExploreOtherCurriculums countryCode={countryCode} />
          </ContentWrapper>

          <ContentWrapper>
            <SubTitle>What is Mathspace</SubTitle>
            <FeaturesSection isDarkMode />
            <Separator size={10} />

            <FreeTextbooksFooterLinks />
          </ContentWrapper>
        </FooterBackground>
      </Route>
    </Wrapper>
  );
}
